import routes from './configs/routes';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { Route } from 'react-router-dom';
import { Switch } from 'react-router-dom';
import { errorLink } from 'libs/apollo/errorLink';
import { httpLink } from 'libs/apollo/httpLink';
import { requestLink } from 'libs/apollo/requestLink';
import { terminalLink } from 'libs/apollo/terminalLink';
import { ApolloClient } from '@apollo/client';
import { ApolloLink } from '@apollo/client';
import { ApolloProvider } from '@apollo/client';
import { InMemoryCache } from '@apollo/client';
import { AlertStack } from 'components/alert/AlertStack';
import { DomainProvider } from 'components/domain/DomainProvider';
import { LoadingProvider } from 'components/loading/LoadingProvider';
import { MediaProvider } from 'components/media/MediaProvider';
import { ModalStack } from 'components/modal/ModalStack';
import { Routing } from 'components/router/Routing';
import { SessionProvider } from 'components/session/SessionProvider';
import { ThemeProvider } from 'components/theme/ThemeProvider';
import { TranslationProvider } from 'components/translation/TranslationProvider';
import { UserProvider } from 'components/user/UserProvider';
import { BreakpointProvider } from 'components/util/BreakpointProvider';
import { PublicLayout } from 'layouts/PublicLayout';
import { React, Suspense } from 'libs/react';
import { lazy } from 'react';
import { LazyLoadFallback } from 'components/loading/LazyLoadFallback';
import 'intl-tel-input/build/css/intlTelInput.css';
import 'styles/main.scss';
import { HelmetProvider } from 'react-helmet-async';
/*
 * The back office and the terminal load on demand, so visitors of the public
 * pages (store, forms, quotes) do not download them.
 */
const RootLayout = lazy(() => import('layouts/RootLayout').then((m) => ({ default: m.RootLayout })));
const TerminalAppScreen = lazy(() => import('modules/terminal/TerminalAppScreen').then((m) => ({
    default: m.TerminalAppScreen
})));
const TerminalPairScreen = lazy(() => import('modules/terminal/TerminalPairScreen').then((m) => ({
    default: m.TerminalPairScreen
})));
/**
 * Initialize the apollo client.
 * @since 1.0.0
 */
const client = new ApolloClient({
    link: ApolloLink.from([
        errorLink,
        requestLink,
        httpLink(process.env.API_URL || '') // Update apollo-upload-client types
    ]),
    cache: new InMemoryCache({
        typePolicies: {
            Session: {
                keyFields: ['token']
            },
            Account: {
                merge: true
            },
            Client: {
                fields: {
                    tags: {
                        merge(_existing, incoming) {
                            return incoming;
                        }
                    }
                }
            },
            Contact: {
                fields: {
                    tags: {
                        merge(_existing, incoming) {
                            return incoming;
                        }
                    }
                }
            }
        }
    })
});
/**
 * The floor app talks to its own endpoint and carries a device token rather
 * than a session, so it gets its own client: nothing here should start
 * working because someone is signed in to the back office in another tab.
 *
 * It deliberately leaves out errorLink. That link treats a refused token as an
 * expired back-office session: it wipes session.token and redirects to
 * /authenticate. On a device that nobody signs in to, a revoked terminal would
 * have been bounced to a login page and would have signed out anyone using the
 * back office in the same browser. The terminal screens handle a refused token
 * themselves, by going back to the pairing form.
 */
const terminalClient = new ApolloClient({
    link: ApolloLink.from([
        terminalLink,
        httpLink(`${process.env.API_URL_ROOT || ''}/terminal`)
    ]),
    cache: new InMemoryCache()
});
const publicClient = new ApolloClient({
    link: ApolloLink.from([
        errorLink,
        requestLink,
        httpLink(process.env.API_URL_PUBLIC || '') // Update apollo-upload-client types
    ]),
    cache: new InMemoryCache({
        typePolicies: {
            Session: {
                keyFields: ['token']
            }
        }
    })
});
// Global error handler for CrossStorage during development
window.addEventListener('unhandledrejection', (event) => {
    if (event.reason &&
        event.reason.message &&
        event.reason.message.includes('CrossStorageClient could not connect')) {
        console.warn('CrossStorage connection failed (development mode) - this is normal during HMR');
        event.preventDefault(); // Prevent the error from being logged
    }
});
/**
 * Renders the asset.
 * @since 1.0.0
 */
ReactDOM.render(React.createElement(HelmetProvider, null,
    React.createElement(BrowserRouter, null,
        React.createElement(BreakpointProvider, null,
            React.createElement(Switch, null,
                React.createElement(Route, { path: "/interactive-quote" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(DomainProvider, null,
                            React.createElement(ModalStack, null,
                                React.createElement(AlertStack, null,
                                    React.createElement(PublicLayout, null)))))),
                React.createElement(Route, { path: "/form" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(MediaProvider, null,
                            React.createElement(DomainProvider, null,
                                React.createElement(ModalStack, null,
                                    React.createElement(AlertStack, null,
                                        React.createElement(PublicLayout, null))))))),
                React.createElement(Route, { path: "/store" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(MediaProvider, null,
                            React.createElement(DomainProvider, null,
                                React.createElement(ModalStack, null,
                                    React.createElement(AlertStack, null,
                                        React.createElement(PublicLayout, null))))))),
                React.createElement(Route, { path: "/portal" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(ModalStack, null,
                            React.createElement(PublicLayout, null)))),
                React.createElement(Route, { path: "/public" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(ModalStack, null,
                            React.createElement(PublicLayout, null)))),
                React.createElement(Route, { path: "/pipedrive" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(ModalStack, null,
                            React.createElement(PublicLayout, null)))),
                React.createElement(Route, { path: "/orders/confirmation" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(ModalStack, null,
                            React.createElement(PublicLayout, null)))),
                React.createElement(Route, { path: "/orders/approval" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(DomainProvider, null,
                            React.createElement(ModalStack, null,
                                React.createElement(AlertStack, null,
                                    React.createElement(PublicLayout, null)))))),
                React.createElement(Route, { path: "/terminal/pair/:code" },
                    React.createElement(ApolloProvider, { client: terminalClient },
                        React.createElement(Suspense, { fallback: React.createElement(LazyLoadFallback, null) },
                            React.createElement(TerminalPairScreen, null)))),
                React.createElement(Route, { path: "/terminal" },
                    React.createElement(ApolloProvider, { client: terminalClient },
                        React.createElement(Suspense, { fallback: React.createElement(LazyLoadFallback, null) },
                            React.createElement(TerminalAppScreen, null)))),
                React.createElement(Route, { path: "/vouchers/status" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(DomainProvider, null,
                            React.createElement(ModalStack, null,
                                React.createElement(AlertStack, null,
                                    React.createElement(PublicLayout, null)))))),
                React.createElement(Route, { path: "/signup" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(DomainProvider, null,
                            React.createElement(SessionProvider, null,
                                React.createElement(ModalStack, null,
                                    React.createElement(AlertStack, null,
                                        React.createElement(PublicLayout, null))))))),
                React.createElement(Route, { path: "/shared-calendar" },
                    React.createElement(ModalStack, null,
                        React.createElement(AlertStack, null,
                            React.createElement(PublicLayout, null)))),
                React.createElement(Route, { path: "/documentation" },
                    React.createElement(ApolloProvider, { client: publicClient },
                        React.createElement(ModalStack, null,
                            React.createElement(AlertStack, null,
                                React.createElement(PublicLayout, null))))),
                React.createElement(Routing, { routes: routes },
                    React.createElement(ApolloProvider, { client: client },
                        React.createElement(LoadingProvider, null,
                            React.createElement(MediaProvider, null,
                                React.createElement(DomainProvider, null,
                                    React.createElement(SessionProvider, null,
                                        React.createElement(UserProvider, null,
                                            React.createElement(ThemeProvider, null,
                                                React.createElement(TranslationProvider, null,
                                                    React.createElement(ModalStack, null,
                                                        React.createElement(AlertStack, null,
                                                            React.createElement(Suspense, { fallback: React.createElement(LazyLoadFallback, null) },
                                                                React.createElement(RootLayout, null))))))))))))))))), document.getElementById('app'));
